import React from 'react';
import { Input } from './ui/input';
import Combobox from './Combobox';
import { Button } from './ui/button';
import NationalitySelector from './NationalitySelector';
import { getSectorList } from '../sectorData';
import { getAll } from '../mapping';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ChevronDown, X } from 'lucide-react';

const SearchForm = ({ 
  filters, 
  handleChange, 
  handleClear, 
  degrees, 
  companies, 
}) => {
  // Convert arrays to format needed for Combobox
  const companiesList = Array.from(companies).map(item => ({
    label: item,
    value: item
  }));

  const sectorsList = Array.from(getSectorList()).map(item => ({
    label: item,
    value: item
  }));

  const academicsList = Array.from(getAll()).map(item => ({
    label: item,
    value: item
  }));

  const currentYear = new Date().getFullYear();
  const gradYearOptions = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const handleNationalityChange = (selectedNationalities) => {
    handleChange({
      target: {
        name: 'nationalities',
        value: selectedNationalities
      }
    });
  };

  return (
    <div className="w-full">
      <div className="flex items-center gap-2 w-full justify-end">
        <div className="hidden sm:block flex-1 min-w-[10vw] max-w-[30vw]">
          <Input
            type="text"
            name="keyword"
            value={filters.keyword}
            onChange={handleChange}
            placeholder="Search name, blurb, or extracurriculars"
            className="w-full"
          />
        </div>
        
        

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 shrink-0">
              Filters
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="bottom" className="max-sm:w-[100vw] sm:w-150 overflow-visible p-4 z-[5000]">
          <DropdownMenuLabel className="hidden sm:block ">Search Filters</DropdownMenuLabel>
            <div className="flex sm:hidden flex-row w-full justify-between">
              <DropdownMenuLabel>Search Filters</DropdownMenuLabel>
              <Button variant="outline" className="gap-2 shrink-0" onClick={handleClear}>
              <X className="h-4 w-4" />
              Clear
              </Button>
            </div>
            <DropdownMenuSeparator />
              <Input
                type="text"
                name="keyword"
                value={filters.keyword}
                onChange={handleChange}
                placeholder="Search name, blurb, or extracurriculars"
                className="block sm:hidden w-full my-4"
              />
            
            <div className="space-y-4">
              {/* Academic Background */}
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1">Academic Background</label>
                <Combobox
                  items={academicsList}
                  name="academics"
                  default="Select Major, Minor, or Certificate"
                  currentState={filters.academics}
                  onChange={handleChange}
                />
              </div>

              {/* Graduation Year Range */}
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1">Grad Year (From)</label>
                  <Input
                    type="number"
                    name="gradYearStart"
                    value={filters.gradYearStart || ''}
                    onChange={handleChange}
                    min={currentYear - 50}
                    max={currentYear + 5}
                    className="w-full"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1">Grad Year (To)</label>
                  <Input
                    type="number"
                    name="gradYearEnd"
                    value={filters.gradYearEnd || ''}
                    onChange={handleChange}
                    min={currentYear - 50}
                    max={currentYear + 5}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Location and Sector */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Current Location</label>
                  <Input
                    type="text"
                    name="currentLocation"
                    value={filters.currentLocation || ''}
                    onChange={handleChange}
                    placeholder="Current Location"
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Sector</label>
                  <Combobox
                    items={sectorsList}
                    name="sector"
                    default="Select Sector"
                    currentState={filters.sector}
                    onChange={handleChange}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Role and Employer */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Role</label>
                  <Input
                    type="text"
                    name="role"
                    value={filters.role || ''}
                    onChange={handleChange}
                    placeholder="Current Role"
                    className="w-full"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium mb-1">Employer</label>
                  <Combobox
                    items={companiesList}
                    name="company"
                    default="Filter by Employer"
                    currentState={filters.company}
                    onChange={handleChange}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Nationality Selector */}
              <div className="flex flex-col">
                <NationalitySelector
                  selectedCodes={filters.nationalities || []}
                  onChange={handleNationalityChange}
                />
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" className="hidden sm:flex gap-2 shrink-0" onClick={handleClear}>
          <X className="h-4 w-4" />
          Clear
        </Button>
      </div>
    </div>
  );
};

export default SearchForm;